import React, { useEffect, useState, useRef } from "react";
import axios from "../../../../api/axios";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import * as signalR from "@microsoft/signalr";

const AdminNotifications = ({ notifications, setNotifications }) => {
  const [loading, setLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);

  const bellRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // -----------------------------------------
  // FETCH NOTIFICATIONS (REST API)
  // -----------------------------------------
  const fetchNotifications = async () => {
    try {
      const token =
        "Bearer " + localStorage.getItem("token")?.replace(/"/g, "");
      const res = await axios.get("/api/dashboard-notifications", {
        headers: { Authorization: token },
      });

      setNotifications(
        res.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      );
    } catch (error) {
      console.error("Failed to fetch notifications", error);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------------------
  // SIGNALR LISTENER (TRIGGER ONLY)
  // -----------------------------------------
  useEffect(() => {
    const token = localStorage.getItem("token")?.replace(/"/g, "");
    if (!token) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl("https://api.omnirep.online/notificationHub", {
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect()
      .build();

    // Trigger only — call fetchNotifications
    connection.on("AdminReportAdded", () => {
      console.log("🔥 AdminReportAdded received");
      fetchNotifications();
    });

    connection.start().catch((err) => console.error(err));
    return () => connection.stop();
  }, []);

  // -----------------------------------------
  // CALL FETCH ON MOUNT
  // -----------------------------------------
  useEffect(() => {
    fetchNotifications();
  }, []);

  // -----------------------------------------
  // MARK ONE AS READ
  // -----------------------------------------
  const markAsRead = async (id) => {
    try {
      const token =
        "Bearer " + localStorage.getItem("token")?.replace(/"/g, "");
      await axios.post(
        `/api/dashboard-notifications/mark-as-read/${id}`,
        null,
        {
          headers: { Authorization: token },
        }
      );
      // Refresh the list
      await fetchNotifications();
    } catch (error) {
      console.error("Failed to mark as read", error);
    }
  };

  // -----------------------------------------
  // MARK ALL AS READ
  // -----------------------------------------
  const markAllAsRead = async () => {
    try {
      const token =
        "Bearer " + localStorage.getItem("token")?.replace(/"/g, "");
      await axios.post(`/api/dashboard-notifications/mark-all-as-read`, null, {
        headers: { Authorization: token },
      });
      await fetchNotifications();
    } catch (error) {
      console.error("Failed to mark all as read", error);
    }
  };

  const handleNotificationClick = async (
    reportId,
    notificationId,
    complaintId
  ) => {
    await markAsRead(notificationId);
    setShowNotifications(false);

    if (complaintId) {
      navigate(`/admin/complaints`);
    } else if (reportId) {
      navigate(`/admin/report`);
    } else {
      navigate(`/admin/payment`);
    }

    // force refresh
    setTimeout(() => {
      window.location.reload();
    }, 0);
  };

  // // -----------------------------------------
  // // CLICK NOTIFICATION → MARK READ + NAVIGATE
  // // -----------------------------------------
  // const handleNotificationClick = async (reportId, notificationId, complaintId) => {
  //   await markAsRead(notificationId);
  //   setShowNotifications(false);

  //   if (complaintId) navigate(`/admin/complaints`);
  //   else if (reportId) navigate(`/admin/report`);
  //   else navigate(`/admin/payment`);
  // };

  // const handleNotificationClick = async (
  //   reportId,
  //   notificationId,
  //   complaintId
  // ) => {
  //   await markAsRead(notificationId);
  //   setShowNotifications(false);

  //   if (complaintId) {
  //     navigate(`/admin/complaints`);
  //   } else if (reportId) {
  //     navigate("/admin/report", {
  //       state: { refresh: Date.now() },
  //     });
  //   } else {
  //     navigate(`/admin/payment`);
  //   }
  // };

  // -----------------------------------------
  // CLOSE DROPDOWN WHEN CLICK OUTSIDE
  // -----------------------------------------
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        bellRef.current &&
        dropdownRef.current &&
        !bellRef.current.contains(event.target) &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <div className="relative flex w-full justify-end">
      <button
        ref={bellRef}
        onClick={() => setShowNotifications((prev) => !prev)}
        className="relative focus:outline-none"
        aria-label="Toggle notifications"
      >
        <Bell className="h-7 w-7" />
        {unreadCount > 0 && (
          <span className="absolute right-0 top-0 rounded-full bg-blue-600 px-2 text-xs font-bold text-white">
            {unreadCount}
          </span>
        )}
      </button>

      {showNotifications && (
        <div
          ref={dropdownRef}
          className="absolute right-0 z-50 mt-6 max-h-96 w-96 overflow-auto rounded-2xl border border-gray-300 bg-white p-4 shadow-lg"
        >
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-xl font-semibold">
              <Bell className="h-5 w-5" /> Njoftimet
            </h2>
            {notifications.length > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  markAllAsRead();
                }}
                className="text-sm font-medium text-gray-600 underline hover:text-gray-800"
              >
                Shëno të gjitha si të lexuara
              </button>
            )}
          </div>

          {loading ? (
            <div className="text-gray-500">Duke i ngarkuar njoftimet...</div>
          ) : notifications.length === 0 ? (
            <div className="text-gray-500">Nuk ka njoftime.</div>
          ) : (
            <ul className="space-y-3">
              {notifications.map(
                ({
                  id,
                  message,
                  reportId,
                  reportName,
                  complaintId,
                  createdAt,
                  isRead,
                }) => {
                  const displayMessage =
                    message && message.trim() !== ""
                      ? message
                      : `Keni pranuar një raport të ri: ${reportName}`;

                  return (
                    <li
                      key={id}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleNotificationClick(reportId, id, complaintId);
                      }}
                      className={`cursor-pointer rounded-xl border p-3 text-sm transition ${
                        isRead
                          ? "border-gray-300 bg-gray-100 text-gray-600"
                          : "border-green-300 bg-green-100 text-gray-800 hover:bg-green-200"
                      }`}
                      title={new Date(createdAt).toLocaleString()}
                    >
                      <div className="flex items-center justify-between">
                        <span>{displayMessage}</span>
                        {!isRead && (
                          <span className="ml-2 text-xs font-semibold text-green-600">
                            new
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {new Date(createdAt).toLocaleString()}
                      </div>
                    </li>
                  );
                }
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminNotifications;
